import { useParams, useSearchParams } from 'react-router-dom';
import BookReader from '@/components/BookPage';
import { useEffect, useState } from "react";
import { api } from "@/services/auth-service";
import StarrySkyBackground from '@/components/StarrySkyBackground';
import { Loader2 } from "lucide-react";
import { Error403, Error404, Error500 } from '@/components/ReadStoryErrorComponents';

// Helper function to preload images
const preloadImage = (url: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => reject();
        img.src = url;
    });
};

const ReadBookPage = () => {
    const [story, setStory] = useState(null);
    const [status, setStatus] = useState('pending');
    const { storyId } = useParams();
    const [searchParams] = useSearchParams();
    const [isPolling, setIsPolling] = useState(true);

    useEffect(() => {
        let timeoutId;
        let isMounted = true;

        const preloadStoryImages = async (storyData) => {
            const imageUrls = storyData.pages
                .filter(page => page.imageUrl)
                .map(page => page.imageUrl);

            await Promise.all(imageUrls.map(url => preloadImage(url)));
        };

        const pollStoryStatus = async () => {
            if (!isPolling) return;
            const sharableLink = searchParams.get('sharable_link')
            const baseUrl = `/api/generated-story/${storyId}/`
            const fetchStoryUrl = sharableLink ? `${baseUrl}?sharable_link=${sharableLink}` : baseUrl

            try {
                const response = await api.get(fetchStoryUrl);
                const data = response.data;

                if (!isMounted) return;

                if (data.status === 'completed') {
                    await preloadStoryImages(data);
                    setStory(data);
                    setStatus('completed');
                    setIsPolling(false); // Stop polling on completed
                } else if (data.status === 'failure') {
                    setStatus('failure');
                    setIsPolling(false); // Stop polling on failure
                } else {
                    timeoutId = setTimeout(pollStoryStatus, 1000);
                }
            } catch (error) {
                if (!isMounted) return;

                // @ts-expect-error ignoring
                if (error.response?.status === 403) {
                    setStatus('forbidden');
                    setIsPolling(false);
                // @ts-expect-error ignoring
                } else if (error.response?.status === 404) {
                    timeoutId = setTimeout(async () => {
                        try {
                            const retryResponse = await api.get(fetchStoryUrl);
                            if (retryResponse.data.status === 'completed') {
                                await preloadStoryImages(retryResponse.data);
                                setStory(retryResponse.data);
                                setStatus('completed');
                            }
                        } catch (retryError) {
                            // @ts-expect-error ignoring
                            if (retryError.response?.status === 404) {
                                timeoutId = setTimeout(async () => {
                                    try {
                                        const finalResponse = await api.get(fetchStoryUrl);
                                        if (finalResponse.data.status === 'completed') {
                                            await preloadStoryImages(finalResponse.data);
                                            setStory(finalResponse.data);
                                            setStatus('completed');
                                        } else {
                                            setStatus('not_found');
                                        }
                                    } catch {
                                        setStatus('not_found');
                                    }
                                    setIsPolling(false);
                                }, 3000);
                            } else {
                                setStatus('not_found');
                                setIsPolling(false);
                            }
                        }
                    }, 3000);
                } else {
                    setStatus('error');
                    setIsPolling(false);
                }
            }
        };

        pollStoryStatus();

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
            setIsPolling(false);
        };
    }, [storyId]);

    if (status === 'pending') {
        return (
            <div className="flex flex-col items-center justify-center h-screen">
                <StarrySkyBackground showAurora={true} showClouds={true} showShootingStar={false} />
                <Loader2 className="w-8 h-8 mb-4 animate-spin" />
                <p className="text-lg">Creating your magical story...</p>
            </div>
        );
    }
    if (status === 'forbidden') return <Error403 />;
    if (status === 'not_found') return <Error404 />;
    if (status === 'error' || status === 'failure') return <Error500 />;

    return (
        <div>
            <StarrySkyBackground showAurora={true} showClouds={true} showShootingStar={false} />
            <BookReader story={story} />
        </div>
    );
};

export default ReadBookPage;